import { useEffect, useRef, useState } from "react";
import {
  useRouterQueryStore,
  ViewTextCopyContent,
  Wrapper,
} from "nystem-components";
import app from "nystem";

const number = (
  value,
  maximumFractionDigits = 2,
  minimumFractionDigits = 0,
) =>
  new Intl.NumberFormat("sv-SE", {
    maximumFractionDigits,
    minimumFractionDigits,
  }).format(value);

const ViewListMonthlySubscriptions = ({ model = {} }) => {
  const { remaining } = model;
  const [from, setFrom] = useRouterQueryStore("subscriptionFrom", "int");
  const [to, setTo] = useRouterQueryStore("subscriptionTo", "int");
  const initialized = useRef(false);
  const fromDate = new Date(from);
  const toDate = new Date(to);
  const month =
    from &&
    to &&
    fromDate.getFullYear() === toDate.getFullYear() &&
    fromDate.getMonth() === toDate.getMonth()
      ? `${toDate.getFullYear()}-${String(toDate.getMonth() + 1).padStart(2, "0")}`
      : undefined;
  const [state, setState] = useState({
    loading: true,
    rows: [],
    totals: { count: 0, amount: 0 },
  });

  useEffect(() => {
    let mounted = true;
    if (!month) {
      if (!initialized.current) {
        initialized.current = true;
        const today = new Date();
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        setFrom(start.getTime());
        setTo(new Date(today.getFullYear(), today.getMonth(), 1).getTime() - 1);
        return () => {
          mounted = false;
        };
      }
      setState({
        loading: false,
        selectMonth: true,
        rows: [],
        totals: { count: 0, amount: 0 },
      });
      return () => {
        mounted = false;
      };
    }
    initialized.current = true;
    setState({ loading: true, rows: [], totals: { count: 0, amount: 0 } });

    Promise.all([
      app.connection.emit({ type: "monthlyTransactionStats", month }),
      app.database.product.search({ count: 100000 }),
      app.database.program.search({ count: 100000 }),
    ])
      .then(([statResult, productResult, programResult]) => {
        if (!mounted) return;

        const products = Object.fromEntries(
          (productResult.data || []).map((item) => [item._id, item]),
        );
        const programs = Object.fromEntries(
          (programResult.data || []).map((item) => [item._id, item]),
        );
        const byProduct = Object.fromEntries(
          (statResult.data || [])
            .filter(({ id }) => products[id])
            .map((value) => {
              const product = products[value.id];
              return [
                value.id,
                {
                  ...value,
                  count: remaining ? value.remainingCount : value.count,
                  amount: remaining ? value.remainingAmount : value.amount,
                  name: product.name || value.id,
                  programs:
                    product.program instanceof Array
                      ? product.program
                      : product.program
                        ? [product.program]
                        : ["unassigned"],
                },
              ];
            })
            .filter(([, value]) => !remaining || value.count > 0),
        );

        const byProgram = {};
        Object.values(byProduct).forEach((product) => {
          product.programs.forEach((id) => {
            const value = byProgram[id] || {
              id,
              name:
                id === "unassigned"
                  ? app.t("No program")
                  : programs[id]?.name || id,
              products: [],
              count: 0,
              amount: 0,
            };
            value.products.push(product);
            value.count += product.count;
            value.amount += product.amount;
            byProgram[id] = value;
          });
        });

        setState({
          loading: false,
          rows: Object.values(byProgram).sort((a, b) =>
            a.name.localeCompare(b.name),
          ),
          totals: Object.values(byProduct).reduce(
            (value, product) => ({
              count: value.count + product.count,
              amount: value.amount + product.amount,
            }),
            { count: 0, amount: 0 },
          ),
        });
      })
      .catch(() => {
        if (mounted)
          setState({
            loading: false,
            error: true,
            rows: [],
            totals: { count: 0, amount: 0 },
          });
      });

    return () => {
      mounted = false;
    };
  }, [month, remaining]);

  return (
    <Wrapper>
      {state.selectMonth ? (
        <Wrapper className={["p-4", "text-gray-400"]}>
          {app.t("Select a month")}
        </Wrapper>
      ) : state.loading ? (
        <Wrapper className={["p-4", "text-gray-400"]}>
          {app.t("Loading")}
        </Wrapper>
      ) : state.error ? (
        <Wrapper className={["p-4", "text-red-400"]}>
          {app.t("Could not load subscription statistics.")}
        </Wrapper>
      ) : (
        <Wrapper renderAs="table" className={["table", "w-full"]}>
          <Wrapper renderAs="thead">
            <Wrapper renderAs="tr">
              <Wrapper renderAs="th">{app.t("Program / product")}</Wrapper>
              <Wrapper renderAs="th" className={["w-32", "text-right"]}>
                {app.t(remaining ? "Months left" : "Subscriptions")}
              </Wrapper>
              <Wrapper renderAs="th" className={["w-32", "text-right"]}>
                {app.t("Amount (kr)")}
              </Wrapper>
            </Wrapper>
          </Wrapper>
          <Wrapper renderAs="tbody">
            {state.rows.map((program) => [
              <Wrapper
                renderAs="tr"
                key={program.id}
                className={[
                  "border-t-8",
                  "border-transparent",
                  "hover:bg-gray-700/70",
                ]}
              >
                <Wrapper renderAs="td" className={["font-semibold"]}>
                  {program.name}
                </Wrapper>
                <Wrapper
                  renderAs="td"
                  className={["text-right", "font-semibold"]}
                >
                  <ViewTextCopyContent
                    model={{ className: ["inline-block"], trim: true }}
                    copyValue={number(program.count, 1)}
                  >
                    {number(program.count, 1)}
                  </ViewTextCopyContent>
                </Wrapper>
                <Wrapper
                  renderAs="td"
                  className={["text-right", "font-semibold"]}
                >
                  <ViewTextCopyContent
                    model={{ className: ["inline-block"], trim: true }}
                    copyValue={number(program.amount, 2, 2)}
                  >
                    {number(program.amount, 2, 2)}
                  </ViewTextCopyContent>
                </Wrapper>
              </Wrapper>,
              ...program.products
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((product) => (
                  <Wrapper
                    renderAs="tr"
                    key={`${program.id}-${product.id}`}
                    className={["hover:bg-gray-700/70"]}
                  >
                    <Wrapper renderAs="td" className={["pl-6"]}>
                      {product.name}
                    </Wrapper>
                    <Wrapper renderAs="td" className={["text-right"]}>
                      <ViewTextCopyContent
                        model={{ className: ["inline-block"], trim: true }}
                        copyValue={number(product.count, 1)}
                      >
                        {number(product.count, 1)}
                      </ViewTextCopyContent>
                    </Wrapper>
                    <Wrapper renderAs="td" className={["text-right"]}>
                      <ViewTextCopyContent
                        model={{ className: ["inline-block"], trim: true }}
                        copyValue={number(product.amount, 2, 2)}
                      >
                        {number(product.amount, 2, 2)}
                      </ViewTextCopyContent>
                    </Wrapper>
                  </Wrapper>
                )),
            ])}
          </Wrapper>
          <Wrapper renderAs="tfoot">
            <Wrapper renderAs="tr" className={["font-bold"]}>
              <Wrapper renderAs="td" className={["text-right"]}>
                {app.t("Total")}
              </Wrapper>
              <Wrapper renderAs="td" className={["text-right"]}>
                <ViewTextCopyContent
                  model={{ className: ["inline-block"], trim: true }}
                  copyValue={number(state.totals.count, 1)}
                >
                  {number(state.totals.count, 1)}
                </ViewTextCopyContent>
              </Wrapper>
              <Wrapper renderAs="td" className={["text-right"]}>
                <ViewTextCopyContent
                  model={{ className: ["inline-block"], trim: true }}
                  copyValue={number(state.totals.amount, 2, 2)}
                >
                  {number(state.totals.amount, 2, 2)}
                </ViewTextCopyContent>
              </Wrapper>
            </Wrapper>
          </Wrapper>
        </Wrapper>
      )}
    </Wrapper>
  );
};

export default ViewListMonthlySubscriptions;
